/**
 * List block — Equal vs Scroll+Bleed by min-width; Scroll mode uses index-driven
 * carousel controls (arrows / nav / pagination / autoplay / loop / click-to-index).
 *
 * Equal vs scroll is decided from the content-box width (list root), not the
 * bled viewport, so enabling bleed cannot flip the mode back (no oscillation).
 */
(function () {
  if (window.__mammotionListJsBound) return;
  window.__mammotionListJsBound = true;

  const PC_MQ = window.matchMedia('(min-width: 1280px)');
  const INTERACTIVE_SELECTOR =
    'a, button, input, textarea, select, label, summary, [data-list-ignore-click]';
  const CARD_ARROW_HOVER_CLASS = 'product-card-block--arrow-hover';

  /**
   * Hover arrows sit above cards, so CSS :hover leaves the card underneath.
   * Read the stack at the pointer and keep that card in the same visual state.
   * @param {HTMLElement} arrow
   * @param {number} clientX
   * @param {number} clientY
   * @returns {HTMLElement | null}
   */
  function cardUnderArrow(arrow, clientX, clientY) {
    const x = Number.isFinite(clientX) ? clientX : arrow.getBoundingClientRect().left + arrow.offsetWidth / 2;
    const y = Number.isFinite(clientY) ? clientY : arrow.getBoundingClientRect().top + arrow.offsetHeight / 2;
    const stack = document.elementsFromPoint(x, y);

    for (const node of stack) {
      if (!(node instanceof Element) || arrow.contains(node) || node === arrow) continue;
      const card = node.closest('.product-card-block');
      if (card) return card;
    }

    return null;
  }

  /**
   * @param {string | undefined} value
   * @param {boolean} fallback
   * @returns {boolean}
   */
  function toBool(value, fallback = false) {
    if (value === undefined || value === null || value === '') return fallback;
    return String(value) === 'true';
  }

  /**
   * @param {string | undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  function toNumber(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  /**
   * @param {HTMLElement} root
   */
  function initListBlock(root) {
    if (root.dataset.listInitialized === 'true') return;
    root.dataset.listInitialized = 'true';

    const viewport = root.querySelector('[data-list-viewport]');
    const track = root.querySelector('[data-list-track]');
    const prevBtn = root.querySelector('[data-list-prev]');
    const nextBtn = root.querySelector('[data-list-next]');
    const navPrevBtn = root.querySelector('[data-list-nav-prev]');
    const navNextBtn = root.querySelector('[data-list-nav-next]');
    const paginationEl = root.querySelector('[data-list-pagination]');
    const scrollEl = viewport || track;
    if (!track || !scrollEl) return;

    const showHoverArrows = toBool(root.dataset.showHoverArrows, true);
    const showNavButtons = toBool(root.dataset.showNavButtons, false);
    const showPagination = toBool(root.dataset.showPagination, false);
    const enableAutoplay = toBool(root.dataset.enableAutoplay, false);
    const autoplayInterval = toNumber(root.dataset.autoplayInterval, 5000);
    const enableLoop = toBool(root.dataset.enableLoop, false);
    const clickToChangeIndex = toBool(root.dataset.clickToChangeIndex, false);

    let activeIndex = 0;
    let isScrollMode = false;
    let isInViewport = true;
    let autoplayTimer = null;
    let scrollSyncRaf = 0;
    let ignoreScrollSync = false;
    let forcedHoverCard = null;

    /**
     * Removes the synthetic card hover used while a list arrow is under the pointer.
     */
    const clearForcedCardHover = () => {
      forcedHoverCard?.classList.remove(CARD_ARROW_HOVER_CLASS);
      forcedHoverCard = null;
    };

    /**
     * Keeps the card under a hover arrow in the same visual hover state as :hover.
     * @param {HTMLElement | null} arrow
     */
    const bindArrowCardHover = (arrow) => {
      if (!arrow) return;

      /**
       * @param {PointerEvent} event
       */
      const syncForcedHover = (event) => {
        const nextCard = cardUnderArrow(arrow, event.clientX, event.clientY);
        if (forcedHoverCard === nextCard) return;
        forcedHoverCard?.classList.remove(CARD_ARROW_HOVER_CLASS);
        forcedHoverCard = nextCard;
        forcedHoverCard?.classList.add(CARD_ARROW_HOVER_CLASS);
      };

      arrow.addEventListener('pointerenter', syncForcedHover);
      arrow.addEventListener('pointermove', syncForcedHover);
      arrow.addEventListener('pointerleave', clearForcedCardHover);
    };

    const getSlides = () => Array.from(track.children);

    const getMetrics = () => {
      const styles = getComputedStyle(root);
      const trackStyles = getComputedStyle(track);
      const minWidth = parseFloat(styles.getPropertyValue('--list-min-block-width')) || 0;
      const gap = parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
      const count = track.children.length;
      return { minWidth, gap, count };
    };

    const getStep = () => {
      const { minWidth, gap } = getMetrics();
      const fallback = track.firstElementChild?.getBoundingClientRect().width || 0;
      return (minWidth || fallback) + gap;
    };

    const getSlideCount = () => track.children.length;

    const canGoPrev = () => {
      const count = getSlideCount();
      if (count <= 1) return false;
      if (enableLoop) return true;
      return activeIndex > 0;
    };

    const canGoNext = () => {
      const count = getSlideCount();
      if (count <= 1) return false;
      if (enableLoop) return true;
      return activeIndex < count - 1;
    };

    /**
     * @param {number} index
     * @param {{ behavior?: ScrollBehavior, restartAutoplay?: boolean }} [options]
     */
    const goTo = (index, options = {}) => {
      const count = getSlideCount();
      if (count <= 0) return;

      let nextIndex = index;
      if (enableLoop) {
        nextIndex = ((index % count) + count) % count;
      } else {
        nextIndex = Math.max(0, Math.min(index, count - 1));
      }

      const wraps =
        enableLoop &&
        count > 1 &&
        ((activeIndex === count - 1 && nextIndex === 0) ||
          (activeIndex === 0 && nextIndex === count - 1 && index < 0));

      const behavior = options.behavior || (wraps ? 'auto' : 'smooth');
      const step = getStep();
      const targetLeft = nextIndex * step;

      activeIndex = nextIndex;
      ignoreScrollSync = true;
      scrollEl.scrollTo({ left: targetLeft, behavior });

      window.setTimeout(
        () => {
          ignoreScrollSync = false;
        },
        behavior === 'smooth' ? 450 : 50
      );

      syncUI();
      if (options.restartAutoplay !== false) {
        restartAutoplay();
      }
    };

    const next = () => {
      if (!canGoNext() && !enableLoop) return;
      goTo(activeIndex + 1);
    };

    const prev = () => {
      if (!canGoPrev() && !enableLoop) return;
      goTo(activeIndex - 1);
    };

    const indexFromScroll = () => {
      const step = getStep();
      const count = getSlideCount();
      if (step <= 0 || count <= 0) return 0;
      return Math.max(0, Math.min(count - 1, Math.round(scrollEl.scrollLeft / step)));
    };

    const setNavDisabled = (button, disabled) => {
      if (!button) return;
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
      button.tabIndex = disabled ? -1 : 0;
    };

    const restartBulletAnimation = () => {
      if (!paginationEl) return;
      const active = paginationEl.querySelector('.list-block__bullet.is-active');
      if (!active) return;
      active.classList.remove('list-block__bullet--restart');
      // force reflow so CSS animation restarts
      void active.offsetWidth;
      active.classList.add('list-block__bullet--restart');
      void active.offsetWidth;
      active.classList.remove('list-block__bullet--restart');
    };

    const syncPagination = () => {
      if (!paginationEl) return;
      const bullets = paginationEl.querySelectorAll('[data-list-bullet]');
      bullets.forEach((bullet, i) => {
        const isActive = i === activeIndex;
        bullet.classList.toggle('is-active', isActive);
        if (isActive) {
          bullet.setAttribute('aria-current', 'true');
        } else {
          bullet.removeAttribute('aria-current');
        }
      });
      if (root.classList.contains('list-block--autoplay-running')) {
        restartBulletAnimation();
      }
    };

    const syncHoverArrows = () => {
      if (!prevBtn || !nextBtn) return;

      const canShow =
        showHoverArrows &&
        PC_MQ.matches &&
        isScrollMode;

      if (!canShow) {
        prevBtn.hidden = true;
        nextBtn.hidden = true;
        clearForcedCardHover();
        return;
      }

      const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
      const canScroll = maxScroll > 2;
      // Keep legacy hidden model (not disabled/grey)
      if (!canScroll) {
        prevBtn.hidden = true;
        nextBtn.hidden = true;
        clearForcedCardHover();
        return;
      }

      if (enableLoop && getSlideCount() > 1) {
        prevBtn.hidden = false;
        nextBtn.hidden = false;
        return;
      }

      const atStart = scrollEl.scrollLeft <= 2;
      const atEnd = scrollEl.scrollLeft >= maxScroll - 2;
      prevBtn.hidden = atStart;
      nextBtn.hidden = atEnd;
    };

    const syncNavButtons = () => {
      if (!showNavButtons) return;
      if (!isScrollMode || getSlideCount() <= 1) {
        setNavDisabled(navPrevBtn, true);
        setNavDisabled(navNextBtn, true);
        return;
      }
      setNavDisabled(navPrevBtn, !canGoPrev());
      setNavDisabled(navNextBtn, !canGoNext());
    };

    const syncUI = () => {
      syncHoverArrows();
      syncNavButtons();
      syncPagination();
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
      root.classList.remove('list-block--autoplay-running');
      syncPagination();
    };

    const startAutoplay = () => {
      stopAutoplay();
      if (!enableAutoplay || !isScrollMode || !isInViewport || getSlideCount() <= 1) {
        return;
      }
      if (!enableLoop && activeIndex >= getSlideCount() - 1) {
        return;
      }

      root.classList.add('list-block--autoplay-running');
      restartBulletAnimation();
      autoplayTimer = window.setInterval(() => {
        if (!enableLoop && activeIndex >= getSlideCount() - 1) {
          stopAutoplay();
          return;
        }
        goTo(activeIndex + 1, { restartAutoplay: false });
        if (enableAutoplay && isScrollMode && isInViewport) {
          // keep running; interval continues
          if (root.classList.contains('list-block--autoplay-running')) {
            restartBulletAnimation();
          }
        }
      }, autoplayInterval);
    };

    const restartAutoplay = () => {
      if (!enableAutoplay || !isScrollMode) {
        stopAutoplay();
        return;
      }
      startAutoplay();
    };

    const teardownCarousel = () => {
      stopAutoplay();
      activeIndex = 0;
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      setNavDisabled(navPrevBtn, true);
      setNavDisabled(navNextBtn, true);
      if (paginationEl) {
        paginationEl.querySelectorAll('[data-list-bullet]').forEach((bullet, i) => {
          bullet.classList.toggle('is-active', i === 0);
        });
      }
    };

    /**
     * Decide scroll mode from list-block content-box width.
     * Bleed is on the child viewport, so root.clientWidth stays stable.
     */
    const updateMode = () => {
      const { minWidth, gap, count } = getMetrics();
      const available = root.clientWidth;
      const equalWidth = count > 0 ? (available - (count - 1) * gap) / count : available;
      const needsScroll = count > 0 && minWidth > 0 && equalWidth < minWidth - 0.5;
      const wasScroll = isScrollMode;
      isScrollMode = needsScroll;
      root.classList.toggle('list-block--scroll', needsScroll);

      if (!needsScroll) {
        teardownCarousel();
        return;
      }

      if (!wasScroll) {
        activeIndex = indexFromScroll();
      }
      syncUI();
      restartAutoplay();
    };

    const onScroll = () => {
      if (!isScrollMode) {
        syncHoverArrows();
        return;
      }
      if (ignoreScrollSync) {
        syncHoverArrows();
        syncNavButtons();
        return;
      }
      if (scrollSyncRaf) cancelAnimationFrame(scrollSyncRaf);
      scrollSyncRaf = requestAnimationFrame(() => {
        const nextIdx = indexFromScroll();
        if (nextIdx !== activeIndex) {
          activeIndex = nextIdx;
          syncUI();
          restartAutoplay();
        } else {
          syncHoverArrows();
          syncNavButtons();
        }
      });
    };

    prevBtn?.addEventListener('click', () => {
      if (!isScrollMode) return;
      if (enableLoop) {
        prev();
      } else {
        goTo(activeIndex - 1);
      }
    });

    nextBtn?.addEventListener('click', () => {
      if (!isScrollMode) return;
      if (enableLoop) {
        next();
      } else {
        goTo(activeIndex + 1);
      }
    });

    bindArrowCardHover(prevBtn);
    bindArrowCardHover(nextBtn);

    navPrevBtn?.addEventListener('click', () => {
      if (!isScrollMode) return;
      prev();
    });

    navNextBtn?.addEventListener('click', () => {
      if (!isScrollMode) return;
      next();
    });

    if (showPagination && paginationEl) {
      paginationEl.addEventListener('click', (event) => {
        if (!isScrollMode) return;
        const bullet = event.target.closest('[data-list-bullet]');
        if (!bullet || !paginationEl.contains(bullet)) return;
        const index = Number(bullet.getAttribute('data-list-bullet-index'));
        if (!Number.isFinite(index)) return;
        goTo(index);
      });
    }

    if (clickToChangeIndex) {
      track.addEventListener('click', (event) => {
        if (!isScrollMode) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest(INTERACTIVE_SELECTOR)) return;

        const slides = getSlides();
        const slide = slides.find((child) => child.contains(target) || child === target);
        if (!slide) return;
        const index = slides.indexOf(slide);
        if (index < 0) return;
        goTo(index);
      });
    }

    scrollEl.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateMode);
    PC_MQ.addEventListener?.('change', updateMode);

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => updateMode());
      ro.observe(root);
    }

    if (enableAutoplay && typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            isInViewport = entry.isIntersecting;
            if (isInViewport) {
              restartAutoplay();
            } else {
              stopAutoplay();
            }
          });
        },
        { threshold: 0.2 }
      );
      io.observe(root);
    }

    requestAnimationFrame(updateMode);
  }

  function initAllLists(scope) {
    const rootScope = scope || document;
    rootScope.querySelectorAll('[data-list-block]').forEach((root) => {
      root.dataset.listInitialized = 'false';
      initListBlock(root);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initAllLists());
  document.addEventListener('shopify:section:load', (event) => initAllLists(event.target));
})();
